// Kabel antar-tab satu browser (BroadcastChannel) — pembungkus TIPIS yang aman
// dipanggil di server/peramban lama: tanpa BroadcastChannel ia menjadi no-op
// (`supported: false`), dan pemanggil jatuh ke perilaku tanpa berbagi.
//
// Sengaja tanpa jaringan: BroadcastChannel hanya antar-konteks origin yang sama
// di perangkat yang sama, jadi tak ada data yang menyeberang akun/perangkat.
// Pesan divalidasi `parse` di sisi penerima — apa pun yang tak lolos dibuang,
// karena origin ini juga dipakai skrip lain (mis. versi aplikasi lama yang
// masih terbuka di tab lain selama deploy).

import type { CountsSnapshot } from "@/lib/adminPollPolicy";

export type TabChannel<T> = {
  /** false = tidak ada BroadcastChannel; post/onMessage tidak melakukan apa-apa. */
  supported: boolean;
  post(msg: T): void;
  /** Daftarkan penerima; mengembalikan fungsi pelepas. Pesan dari tab ini
   *  sendiri tidak pernah dikirim balik (semantik BroadcastChannel). */
  onMessage(handler: (msg: T) => void): () => void;
  close(): void;
};

type ChannelCtor = new (name: string) => {
  postMessage(data: unknown): void;
  close(): void;
  onmessage: ((ev: { data: unknown }) => void) | null;
};

/**
 * `ctor`: `null` = PAKSA tanpa BroadcastChannel. Sengaja `null`, bukan
 * `undefined`: `undefined` memicu nilai bawaan (BroadcastChannel global), jadi
 * jalur "konstruktor melempar → no-op" di bawah akan memanggil dirinya sendiri
 * dengan konstruktor yang sama dan berulang tanpa akhir.
 */
export function createTabChannel<T>(
  name: string,
  parse: (raw: unknown) => T | null,
  ctor: ChannelCtor | null = (globalThis as { BroadcastChannel?: ChannelCtor })
    .BroadcastChannel ?? null
): TabChannel<T> {
  if (!ctor) {
    return {
      supported: false,
      post() {},
      onMessage() {
        return () => {};
      },
      close() {},
    };
  }

  let channel: InstanceType<ChannelCtor> | null = null;
  try {
    channel = new ctor(name);
  } catch {
    channel = null;
  }
  if (!channel) {
    return createTabChannel(name, parse, null);
  }

  const handlers = new Set<(msg: T) => void>();
  channel.onmessage = (ev) => {
    const msg = parse(ev.data);
    if (msg === null) return;
    for (const h of [...handlers]) h(msg);
  };

  return {
    supported: true,
    post(msg) {
      try {
        channel?.postMessage(msg);
      } catch {
        /* kanal sudah ditutup / payload tak bisa diklon — abaikan */
      }
    },
    onMessage(handler) {
      handlers.add(handler);
      return () => {
        handlers.delete(handler);
      };
    },
    close() {
      handlers.clear();
      try {
        channel?.close();
      } catch {
        /* sudah tertutup */
      }
      channel = null;
    },
  };
}

// ─── Protokol badge admin ────────────────────────────────────────────────────

export const ADMIN_COUNTS_CHANNEL = "kelolify-admin-counts";

export type AdminCountsMessage =
  | { t: "hello"; from: string }
  | { t: "claim"; from: string }
  | { t: "snapshot"; from: string; snap: CountsSnapshot };

const isFiniteNum = (v: unknown): v is number =>
  typeof v === "number" && Number.isFinite(v);

export function parseAdminCountsMessage(raw: unknown): AdminCountsMessage | null {
  if (!raw || typeof raw !== "object") return null;
  const m = raw as Record<string, unknown>;
  if (typeof m.from !== "string" || m.from === "") return null;
  if (m.t === "hello") return { t: "hello", from: m.from };
  if (m.t === "claim") return { t: "claim", from: m.from };
  if (m.t === "snapshot") {
    const s = m.snap as Record<string, unknown> | null | undefined;
    if (!s || typeof s !== "object") return null;
    if (
      typeof s.userId !== "string" ||
      s.userId === "" ||
      !isFiniteNum(s.at) ||
      !isFiniteNum(s.invoice) ||
      !isFiniteNum(s.topup) ||
      !isFiniteNum(s.unread)
    ) {
      return null;
    }
    return {
      t: "snapshot",
      from: m.from,
      snap: {
        at: s.at,
        userId: s.userId,
        invoice: s.invoice,
        topup: s.topup,
        unread: s.unread,
      },
    };
  }
  return null;
}

// ─── Protokol invalidasi setelan ─────────────────────────────────────────────

export const SITE_SETTINGS_CHANNEL = "kelolify-site-settings";

export type SiteSettingsMessage = { t: "saved"; from: string };

export function parseSiteSettingsMessage(raw: unknown): SiteSettingsMessage | null {
  if (!raw || typeof raw !== "object") return null;
  const m = raw as Record<string, unknown>;
  if (m.t !== "saved" || typeof m.from !== "string" || m.from === "") return null;
  return { t: "saved", from: m.from };
}
